import React, { useCallback } from 'react'
import {
  faClipboardCheck,
  faShareFromSquare,
  faTag,
} from '@fortawesome/free-solid-svg-icons'

import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import TimeBar from 'app/common/styles/TimeBar'
import ScanIcon from 'client/utils/icons/scan-icon'
import XMarkIcon from 'client/utils/icons/xmark-icon'
import _ from 'lodash'
import {
  $DCDangerColor,
  $DCDangerLightColor,
  $DCInfoColor,
  $DCInfoLightColor,
  $DCprimaryColor,
  $DCprimaryLightColor,
  $DCsecondaryColor,
  $DCsecondaryLightColor,
  $DCSuccessColor,
  $DCSuccessLightColor,
  $DCWarningColor,
  $DCWarningLightColor,
  DcBreadcrumbs,
  DcButton,
  DcDiv,
  DcFlexBox,
  DcInput,
  DcOverlaySlider,
  DcTag,
  PixelDate,
  PixelFactoryContext,
  PixelIcon,
  PixelPageContainer,
  PixelTable,
  PixelTableAction,
  Section,
  SectionActions,
  SectionContent,
} from 'pixel-base'
import DcScanner from 'pixel-base/components/dc-scanner'
import { AjaxResponse } from 'rxjs/ajax'
import { useApi } from 'shared/hooks'
import styled from 'styled-components'
import {
  get,
  getReturnOrders,
  put,
  updateReturnAction,
} from 'utils/api-routes.util'

import {
  IDispatchCourier,
  IPixelFactory,
  IReturnOrder,
} from '../../../../../../decl.d'
import ResendOrderPopup from '../Received/components/ResendOrderPopup'

const breadcrumbs = [
  {
    name: 'Orders',
    active: false,
    link: '/orders',
  },
  {
    name: 'Returns Dashboard',
    active: false,
    link: '/orders/returns',
  },
  {
    name: 'Not Received Returns',
    active: true,
  },
]

interface IApiResponse {
  data: IReturnOrder[]
  _meta: {
    total: number
  }
}

const Returning = () => {
  const [showScanner, setShowScanner] = React.useState(false)
  const [scannedTrackingId, setScannedTrackingId] = React.useState('')
  const scannerRef = React.useRef<HTMLVideoElement>(null)
  const {
    showSuccess,
    pixelConfirm,
    showPageLoader,
    hidePageLoader,
    showFailure,
  } = React.useContext<IPixelFactory>(PixelFactoryContext)
  const [selectedDispatch, setSelectedDispatch] = React.useState<
    Partial<IReturnOrder>
  >({})
  const [data, setData] = React.useState<IReturnOrder[]>([])
  const [updatingData, setUpdatingData] = React.useState<{
    order: Partial<IReturnOrder>
    status: string
  }>({
    order: {},
    status: '',
  })
  const [dispatches, setDispatches] = React.useState<IDispatchCourier[]>([])
  const [filters, setFilters] = React.useState<{
    page: number
    search: string
    type: string
  }>({
    page: 0,
    search: '',
    type: 'returning',
  })
  const [searchValue, setSearchValue] = React.useState('')
  const handleDebounceFn = (value) => {
    setResetPaginationToggle(value.length > 0)
    setFilters({
      ...filters,
      search: value,
    })
  }
  useApi<{ data: IDispatchCourier[] }>(
    () => get(`/courier/${selectedDispatch.courier_id}/dispatch`),
    {
      onSuccess: (res) => {
        const response = res as {
          data: IDispatchCourier[]
        }
        setDispatches(response?.data || [])
      },
      skip: !selectedDispatch?.courier_id,
      deps: [selectedDispatch?.courier_id],
    },
  )

  const debounceFn = useCallback(_.debounce(handleDebounceFn, 1000), [])
  const [totalRows, setTotalRows] = React.useState(0)
  const [showModal, setShowModal] = React.useState(false)
  const [resetPaginationToggle, setResetPaginationToggle] =
    React.useState(false)

  const { data: ordersData, loading: tableLoading } = useApi<IApiResponse>(
    () => getReturnOrders(filters),
    {
      deps: [filters],
    },
  )

  React.useEffect(() => {
    if (ordersData) {
      setData(ordersData.data || [])
      setTotalRows(ordersData._meta?.total || 0)
    }
  }, [ordersData])

  const options = [
    {
      label: 'In Transit',
      disabled: false,
      clickHandler: (row) => {
        changeStatus(row, 'in-transit', 'In Transit')
      },
    },
    {
      label: 'Delivered',
      disabled: false,
      clickHandler: (row) => {
        changeStatus(row, 'delivered', 'Delivered')
      },
    },
  ]

  const changeStatus = (row, status, label) => {
    pixelConfirm({
      title: 'Return not expected',
      message: `Do you want to update the order #  ${row.number} status to ${label}?`,
      onConfirm: () => {
        const obj = {
          action_type: 'status',
          status: status,
          id: row.number,
        }

        updateReturnAction(obj, 'status').subscribe(
          (res: AjaxResponse<any>) => {
            if (res.response.success) {
              showSuccess(res.response.message)
              setUpdatingData({
                order: row,
                status: 'Updated!',
              })
            } else {
              showFailure(res.response.error)
            }
          },
        )
      },
    })
  }
  const actions = [
    {
      title: 'Item Received & Resend',
      icon: (
        <Section>
          <PixelIcon icon={faShareFromSquare} />
        </Section>
      ),
      clickHandler: (row) => {
        pixelConfirm({
          title: 'Confirmation',
          message: `Have you received the return for order ${row.number} ${
            row.shipper ? 'from ' + row.shipper : ''
          } and resending it to the customer with same courier?`,
          onConfirm: () => {
            setSelectedDispatch(row)
            setShowModal(true)
          },
        })
      },
    },
    {
      title: 'Item Received',
      icon: (
        <Section>
          <PixelIcon icon={faClipboardCheck} />
        </Section>
      ),
      clickHandler: (row) => {
        pixelConfirm({
          title: 'Confirm return received',
          message: `Have you received the return for order ${row.number} ${
            row.shipper ? 'from ' + row.shipper : ''
          }`,
          onConfirm: () => {
            const obj = {
              action_type: 'mark_return_received',
              id: row.number,
            }

            updateReturnAction(obj, 'status').subscribe(
              (res: AjaxResponse<any>) => {
                if (res.response.success) {
                  showSuccess(res.response.message)
                  setUpdatingData({
                    order: row,
                    status: 'Updated!',
                  })
                }
              },
            )
          },
        })
      },
    },
    {
      title: 'Change Status',
      type: 'dropdown',
      options: options,
      icon: (
        <Section>
          <PixelIcon icon={faTag} />
        </Section>
      ),
      clickHandler: () => {},
    },
  ]

  const scanningoOrder = (trackingId: string) => {
    showPageLoader()
    const obj = {
      action_type: 'mark_return_received',
      id: trackingId,
    }
    updateReturnAction(obj, 'scan').subscribe((res: AjaxResponse<any>) => {
      hidePageLoader()
      setScannedTrackingId('')
      if (res.response.success) {
        showSuccess(res.response.message)
        const orderData = data.find((item) => item.tracking_id === trackingId)
        if (orderData) {
          setUpdatingData({
            order: orderData,
            status: 'Updated!',
          })
        }
      } else {
        showFailure(res.response.error)
      }
    })
  }

  const handleScannerScan = (trackingId: string) => {
    setScannedTrackingId(trackingId)
    scanningoOrder(trackingId)
  }

  // Manual input handler for testing
  const handleManualInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && e.currentTarget.value.trim()) {
      const trackingId = e.currentTarget.value.trim()
      setScannedTrackingId(trackingId)
      scanningoOrder(trackingId)
      e.currentTarget.value = ''
    }
  }
  React.useEffect(() => {
    if (updatingData.status) {
      setData(
        data.map((item) => {
          if (item.number === updatingData.order.number) {
            item.status = 'Updated!'
          }
          return item
        }),
      )
    }
  }, [updatingData])

  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <Filters>
        <AlertCoantainer>
          <DcButton
            variant={'outline-secondary'}
            size="sm"
            onClick={() => {
              setShowScanner(true)
            }}
            withIcon={true}
          >
            <ScanIcon size={16} />
            <span>Scan Orders</span>
          </DcButton>
        </AlertCoantainer>
        <FilterInput>
          <DcInput
            inputSize="small"
            placeholder="Search"
            name="Search"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value)

              debounceFn(e.target.value)
            }}
          />
        </FilterInput>
      </Filters>
    )
  }, [filters, searchValue, resetPaginationToggle])

  const handlePageChange = (page) => {
    setFilters({
      ...filters,
      page: page - 1,
    })
  }
  const columns = [
    {
      dataKey: 'order_id',
      name: 'Ref #',
      width: '7%',
      sortable: true,
      selector: (row: IReturnOrder) => row.source_order_id,
    },
    {
      dataKey: 'tracking_id',
      name: 'CN #',
      width: '15%',
      sortable: true,
      selector: (row: IReturnOrder) => row.tracking_id,
    },
    {
      dataKey: 'customer_name',
      name: 'Consignee',
      // width: '15%',
      sortable: true,
      selector: (row: IReturnOrder) => row.first_name + ' ' + row.last_name,
    },
    {
      dataKey: 'status',
      name: 'Status',
      // width: '10%',
      sortable: true,
      selector: (row: IReturnOrder) =>
        row.status !== 'Updated!' ? (
          <DcTag
            textColor={
              row.status == 'arrived'
                ? $DCDangerColor
                : row.status === 'return-in-transit'
                ? $DCSuccessColor
                : row.status === 'refused'
                ? $DCprimaryColor
                : row.status === 'attempt'
                ? $DCWarningColor
                : row.status === 'awaiting'
                ? $DCsecondaryColor
                : row.status === 'undefined'
                ? $DCInfoColor
                : undefined
            }
            customBackgroundColor={
              row.status == 'arrived'
                ? $DCDangerLightColor
                : row.status === 'return-in-transit'
                ? $DCSuccessLightColor
                : row.status === 'refused'
                ? $DCprimaryLightColor
                : row.status === 'attempt'
                ? $DCWarningLightColor
                : row.status === 'awaiting'
                ? $DCsecondaryLightColor
                : row.status === 'undefined'
                ? $DCInfoLightColor
                : undefined
            }
          >
            {row.status}
          </DcTag>
        ) : (
          <UpdatedStatus>{row.status}</UpdatedStatus>
        ),
    },
    {
      dataKey: 'shipper',
      name: 'shipper',
      // width: '10%',
      sortable: true,
      selector: (row: IReturnOrder) => row.shipper,
    },
    {
      dataKey: 'date_created',
      name: 'Order Date',
      // width: '10%',
      sortable: true,
      selector: (row: IReturnOrder) => (
        <PixelDate
          className="textSmall"
          value={row?.date_created}
          format={'pixelStandard'}
        />
      ),
    },
    {
      dataKey: 'actions',
      name: 'Actions',
      // width: '15%',
      sortable: true,
      style: {
        'div:first-child': {
          overflow: 'visible !important',
        },
      },
      selector: (row: IReturnOrder) =>
        row.status !== 'Updated!' ? (
          <PixelTableAction row={row} action={actions} />
        ) : (
          '--'
        ),
    },
  ]

  return (
    <DcFlexBox flexDirection="column">
      <PixelPageContainer padding="0px" maxHeight="100vh">
        <Section style={{ padding: '0px' }}>
          <SectionContent style={{ padding: '0px', borderRadius: '0px' }}>
            <DcFlexBox
              padding="0px 20px"
              height="60px"
              style={{
                justifyContent: 'space-between',
                // borderBottom: '1px solid #e5e5e5',
              }}
              alignItems="center"
            >
              <DcFlexBox alignItems="center" width="auto">
                <DcBreadcrumbs fontSize="16px" data={breadcrumbs} />
                <SectionActions />
              </DcFlexBox>
              <DcDiv>
                <ProfileSection />
              </DcDiv>
            </DcFlexBox>
          </SectionContent>
        </Section>
        <Section style={{ padding: '20px' }}>
          <SectionContent style={{ padding: '10px' }}>
            <PixelTable
              fixedHeader
              fixedHeaderScrollHeight="calc(100vh - 230px)"
              pagination={true}
              columns={columns}
              progressPending={tableLoading}
              data={data}
              subHeaderComponent={subHeaderComponentMemo}
              paginationResetDefaultPage={resetPaginationToggle}
              paginationServer
              paginationTotalRows={totalRows}
              highlightOnHover={false}
              isSearchable={true}
              pointerOnHover={false}
              paginationPerPage={50}
              onChangePage={handlePageChange}
              customStyles={{
                subHeader: {
                  style: {
                    // maxheight: isMobile ? '0px !important' : 'auto',
                    // minHeight: isMobile ? '0px !important' : 'auto',
                    padding: '0px !important',
                  },
                },
              }}
              paginationRowsPerPageOptions={[50, 100, 250, 500]}
            />
          </SectionContent>
        </Section>
      </PixelPageContainer>
      {showModal && (
        <DcOverlaySlider
          title={`Received and Resend Order # ${selectedDispatch?.source_order_id}`}
          onClose={() => setShowModal(false)}
          isOpen={showModal}
          width="50%"
        >
          {showModal && (
            <ResendOrderPopup
              selectedDispatch={selectedDispatch}
              dispatches={dispatches}
              origin="return"
              handleClose={(data) => {
                data['tracking_id'] = selectedDispatch['tracking_id']
                data['action_type'] = 'mark_return_received_and_resend'
                data['id'] = selectedDispatch?.number
                showPageLoader()
                put('/order/returns/status', data).subscribe((res) => {
                  const response = res.response as {
                    success: boolean
                    resend: {
                      error: string
                    }
                  }
                  hidePageLoader()
                  if (res.response.success) {
                    showSuccess(
                      `Order received ${
                        !response.resend.error && 'and resend'
                      } successfully`,
                    )
                    if (response.resend.error) {
                      showFailure(response.resend.error)
                    }
                    setUpdatingData({
                      order: {
                        number: selectedDispatch.number,
                      },
                      status: 'Received & Resend!',
                    })
                  } else {
                    showFailure(response.error || 'Failed to resend order')
                  }
                  setShowModal(false)
                })
              }}
            />
          )}
        </DcOverlaySlider>
      )}

      {/* Scanner Modal */}
      <DcScanner
        isOpen={showScanner}
        onClose={() => {
          setShowScanner(false)
          setScannedTrackingId('')
        }}
        scannedSKU={scannedTrackingId}
        setScannedSKU={setScannedTrackingId}
        handleScan={handleScannerScan}
        scannerRef={scannerRef}
      />
    </DcFlexBox>
  )
}

const Filters = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  align-content: center;
`
const FilterInput = styled.div`
  width: 30%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  align-content: flex-start;
  gap: 10px;
`

const UpdatedStatus = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: green;
`
const AlertCoantainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  align-content: flex-start;
  gap: 10px;
  width: 100%;
`

export default Returning
